import React, { useState, useEffect } from 'react';
import BasePOIModal from './BasePOIModal';
import { MeasurementSelector } from './components/MeasurementSelector';
import { NotesAndPhotos } from './components/NotesAndPhotos';
import { WidthClearance } from './components/WidthClearance';

const LINE_TYPES = [
  { value: 'high_voltage', label: 'High Voltage' },
  { value: 'medium_voltage', label: 'Medium Voltage' },
  { value: 'low_voltage', label: 'Low Voltage' },
  { value: 'optical_fiber', label: 'Optical Fiber' },
  { value: 'public_utility', label: 'Public Utility' }
];

const SUPPORT_TYPES = [
  { value: 'wood_pole', label: 'Wood Pole' },
  { value: 'steel_pole', label: 'Steel Pole' },
  { value: 'lattice_tower', label: 'Lattice Tower' },
  { value: 'concrete_pole', label: 'Concrete Pole' }
];

export const PowerLinePOIModal = ({ type, poiInfo, onClose, onSave, onBack }) => {
  const [formData, setFormData] = useState({
    lineType: '',
    voltage: '',
    supportType: '',
    utilityCompany: '',
    touchLevel: null,
    measurementType: 'touch',
    customHeight: { feet: '', inches: '', meters: '' },
    width: { feet: '', inches: '', meters: '' },
    notes: '',
    photos: []
  });

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {};
    if (!formData.lineType) errors.lineType = 'Line type is required';
    if (!formData.supportType) errors.supportType = 'Support type is required';
    if (formData.measurementType === 'touch' && !formData.touchLevel) {
      errors.measurements = 'Touch level is required';
    }
    if (formData.measurementType === 'custom' && 
        !formData.customHeight.feet && 
        !formData.customHeight.meters) {
      errors.measurements = 'Height measurement is required';
    }

    if (Object.keys(errors).length > 0) {
      return { success: false, errors };
    }

    // Prepare measurements array
    const measurements = [];
    if (formData.measurementType === 'touch') {
      measurements.push({
        type: 'touch',
        value: formData.touchLevel,
        unit: 'inches'
      });
    } else {
      if (formData.customHeight.meters) {
        measurements.push({
          type: 'custom',
          value: parseFloat(formData.customHeight.meters),
          unit: 'meters'
        });
      } else {
        measurements.push({
          type: 'custom',
          value: parseInt(formData.customHeight.feet),
          unit: 'feet'
        });
        if (formData.customHeight.inches) {
          measurements.push({
            type: 'custom',
            value: parseInt(formData.customHeight.inches),
            unit: 'inches'
          });
        }
      }
    }

    return onSave({
      measurements,
      notes: formData.notes,
      photos: formData.photos,
      powerLineDetails: {
        lineType: formData.lineType,
        voltage: formData.voltage || null,
        supportType: formData.supportType,
        utilityCompany: formData.utilityCompany || null
      }
    });
  };

  return (
    <BasePOIModal
      type={type}
      poiInfo={poiInfo}
      onClose={onClose}
      onSave={handleSubmit}
      onBack={onBack}
    >
      <div className="space-y-6">
        <MeasurementSelector
          measurementType={formData.measurementType}
          setMeasurementType={(type) => setFormData(prev => ({ ...prev, measurementType: type }))}
          touchLevel={formData.touchLevel}
          setTouchLevel={(level) => setFormData(prev => ({ ...prev, touchLevel: level }))}
          customHeight={formData.customHeight}
          setCustomHeight={(height) => setFormData(prev => ({ ...prev, customHeight: height }))}
        />

        <WidthClearance 
          width={formData.width}
          setWidth={(width) => setFormData(prev => ({ ...prev, width }))}
        />

        <div>
          <label className="block text-gray-400 mb-2">Line Type</label>
          <select
            value={formData.lineType}
            onChange={(e) => setFormData(prev => ({ ...prev, lineType: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">Select Line Type</option>
            {LINE_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Voltage (if known)</label>
          <input
            type="text"
            value={formData.voltage}
            onChange={(e) => setFormData(prev => ({ ...prev, voltage: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            placeholder="Enter voltage (e.g., 69kV, 138kV, etc.)"
          />
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Support Type</label>
          <select
            value={formData.supportType}
            onChange={(e) => setFormData(prev => ({ ...prev, supportType: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">Select Support Type</option>
            {SUPPORT_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-400 mb-2">Utility Company (if known)</label>
          <input
            type="text"
            value={formData.utilityCompany}
            onChange={(e) => setFormData(prev => ({ ...prev, utilityCompany: e.target.value }))}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
            placeholder="Enter utility company name"
          />
        </div>

        <NotesAndPhotos
          notes={formData.notes}
          setNotes={(notes) => setFormData(prev => ({ ...prev, notes }))}
          photos={formData.photos}
          setPhotos={(photos) => setFormData(prev => ({ ...prev, photos }))}
        />
      </div>
    </BasePOIModal>
  );
};

export default PowerLinePOIModal;